
import DashboardAssessmentCard, {
  AssessmentCardType
} from "@/components/candidate/DashboardAssessmentCard.vue";
import HelloUser from "@/components/shared/HelloUser.vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import {
  GET_COMPANY_DETAILS,
  GET_USER_DETAILS,
  UPDATE_USER_DETAILS
} from "@/store/modules/auth/constants";
import { CHECK_SURVEY_ALREADY_SUBMITTED } from "@/store/modules/candidates/constants";
import {
  APP_ASSESSMENTS,
  APP_ASSESSMENT_RESULT
} from "@/store/modules/common/constants";
import { REDO_ASSESSMENT_REQUEST } from "@/store/modules/recruiter/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import AssessmentPersistenceService from "@/services/assessment-persistence.service";
import { resolveEnglishAssessmentTypeByPersona } from "@/utils/assessment_persona";
import { get_assigned_assessments } from "@/store/apis/candidate";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";

type AssessmentAssignment = {
  assessment_id: number;
  assessment_type: string;
  start_at: string | null;
  expires_at: string | null;
  availability_status: "AVAILABLE" | "UPCOMING" | "EXPIRED";
};

export default Vue.extend({
  name: "InceptionCandidateDashboard",
  components: {
    HelloUser,
    DashboardAssessmentCard
  },
  data() {
    return {
      dialog: {
        value: false
      },
      survey_popup: false,
      survey_already_submitted: false,
      is_both_assessment_completed: false,
      assessments: [] as AssessmentCardType[],
      refetchKey: 0
    };
  },
  created() {
    // this.show_intro();
  },
  async mounted() {
    this.loadAssessments();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    // Refetch assessment status so we never show "Resume" after submit (English or Additional).
    // Backend may still be updating on first load, so refetch multiple times.
    setTimeout(() => {
      this.refetchKey += 1;
    }, 500);
    setTimeout(() => {
      this.refetchKey += 1;
    }, 1500);
    setTimeout(() => {
      this.refetchKey += 1;
    }, 3500);
    this.survey_already_submitted = await this.check_survey_submitted();
    if (this.survey_already_submitted) {
      this.survey_popup = false;
    } else {
      this.survey_popup = this.is_both_assessment_completed;
    }

    // Only clear localStorage when assessment is cancelled (not in progress)
    // Check if there's a cancelled assessment flag
    const assessmentCancelled = localStorage.getItem("assessment_cancelled");
    if (assessmentCancelled === "1") {
      // Assessment was cancelled, clear all localStorage
      this.clearAssessmentLocalStorage();
    }
    // Don't clear localStorage on normal page load - let the assessment resume
    // The persistence service handles session management
  },
  beforeDestroy() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },
  watch: {
    assessments: {
      handler: async function (newAssessments: AssessmentCardType[]) {
        const engAssessment = newAssessments.find(
          (assessment) => assessment.type === "english"
        );
        const annAssessment = newAssessments.find(
          (assessment) => assessment.type === "annotation"
        );

        if (
          engAssessment &&
          annAssessment &&
          engAssessment.status ===
            AppAssessments.AppAssessmentStatus.COMPLETED &&
          annAssessment.status === AppAssessments.AppAssessmentStatus.COMPLETED
        ) {
          this.is_both_assessment_completed = true;
          if (!this.survey_already_submitted) {
            this.survey_popup = true;
          }
        }
      },
      deep: true,
      immediate: true
    },
    "$i18n.locale"() {
      this.reloadloadAssessments();
    }
  },
  // async beforeRouteLeave(to, from, next) {
  // const thisself = this;
  // console.log("f2 leave");
  // Convert `this.response` to a string before storing it
  // localStorage.setItem("leaving_stage", this.response);
  // console.log("Saved response to localStorage");
  // next();
  // },
  computed: {
    ...mapGetters("auth", {
      get_user_details: GET_USER_DETAILS
    }),
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS
    }),
    ...mapGetters("auth", {
      get_company_details: GET_COMPANY_DETAILS
    }),
    isArabic() {
      return localStorage.getItem("locale") === "ar";
    }
  },
  methods: {
    handleVisibilityChange() {
      if (!document.hidden) {
        this.refetchKey += 1;
      }
    },
    ...mapActions("common", {
      app_assessment_result: APP_ASSESSMENT_RESULT
    }),
    ...mapActions("auth", {
      update_user_details: UPDATE_USER_DETAILS
    }),
    ...mapActions("recruiter", {
      redo_assessment_request: REDO_ASSESSMENT_REQUEST
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    ...mapActions("candidate", {
      check_survey_submitted: CHECK_SURVEY_ALREADY_SUBMITTED
    }),
    parseDefaultAssessments(settingValue?: string): string[] {
      const parsed = String(settingValue || "")
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter((item) => item.startsWith("ai:"));
      if (
        parsed.some((item) => item.startsWith("ai:")) &&
        !parsed.includes("ai")
      )
        parsed.push("ai");
      return parsed;
    },
    parseLegacyDefaultTypes(settingValue?: string): string[] {
      return String(settingValue || "")
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    },
    async loadAssessments() {
      // Two tenant models, selected by data (not by company id):
      //  - Config-driven (e.g. GT): assessments come from company assessment_setting.
      //  - Assignment-driven (e.g. people-grid): assessments come from the
      //    candidate's user_assessment_assignments.
      const hasConfig =
        !!this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_english;
      if (hasConfig) {
        this.loadAssessmentsFromConfig();
      } else {
        await this.loadAssessmentsFromAssignments();
      }
    },
    loadAssessmentsFromConfig() {
      const settingValue =
        this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_default_assessments;
      const legacyTypes = this.parseLegacyDefaultTypes(settingValue);
      const assessments: AssessmentCardType[] = [
        this.buildAssessmentCard(
          "english",
          resolveEnglishAssessmentTypeByPersona(
            this.get_user_details?.persona,
            this.get_company_details?.storage_path?.assessment_setting
              ?.candidates_english
          )
        ),
        this.buildAssessmentCard("ai", "")
      ];
      if (legacyTypes.includes("annotation")) {
        assessments.push(
          this.buildAssessmentCard(
            "annotation",
            this.get_company_details?.storage_path?.assessment_setting
              ?.candidates_annotation
          )
        );
      }
      this.assessments = assessments;
    },
    async loadAssessmentsFromAssignments() {
      try {
        const response = await get_assigned_assessments();
        const assigned = response?.data?.data || [];
        const cards: AssessmentCardType[] = [];
        const additional: AssessmentAssignment[] = [];
        for (const item of assigned) {
          const type = String(item.assessment_type || "").toLowerCase();
          if (type.startsWith("english")) {
            cards.push(
              this.buildAssessmentCard("english", item.assessment_type, item)
            );
          } else if (type.startsWith("annotation")) {
            cards.push(
              this.buildAssessmentCard("annotation", item.assessment_type, item)
            );
          } else {
            // Behavioral / Online Assessment / Quiz → the "additional" (ai) flow,
            // which aggregates all such assignments into one card.
            additional.push(item);
          }
        }
        if (additional.length) {
          // Aggregate window for the single "ai" card: available if any is available.
          const anyAvailable = additional.find(
            (a) => a.availability_status === "AVAILABLE"
          );
          cards.push(
            this.buildAssessmentCard("ai", "", anyAvailable || additional[0])
          );
        }
        this.assessments = cards;
      } catch (error) {
        this.assessments = [];
      }
    },
    buildAssessmentCard(
      type: "english" | "annotation" | "ai",
      version?: string,
      assignment?: AssessmentAssignment
    ): AssessmentCardType {
      const meta: Record<
        string,
        { title: string; description: string; icon: string }
      > = {
        english: {
          title: this.$t("assessments.eng-title") as string,
          description: this.$t("assessments.eng-description") as string,
          icon: require("@/assets/icons/inception_eng_assessment.svg")
        },
        annotation: {
          title: this.$t("assessments.annotation-title") as string,
          description: this.$t("assessments.annotation-description") as string,
          icon: require("@/assets/icons/inception_annotation_assessment.svg")
        },
        ai: {
          title: this.$t("assessments.ai-title") as string,
          description: this.$t("assessments.ai-description") as string,
          icon: require("@/assets/icons/inception_ai_assessments.png")
        }
      };
      return {
        status: "NOT_STARTED" as string,
        is_show: true,
        type,
        title: meta[type].title,
        description: meta[type].description,
        icon: meta[type].icon,
        version: version || "",
        availability_status: assignment?.availability_status || "AVAILABLE",
        start_at: assignment?.start_at || null,
        expires_at: assignment?.expires_at || null
      };
    },
    reloadloadAssessments() {
      this.assessments = this.assessments.map((assessment) => {
        if (assessment.type === "english") {
          return {
            ...assessment,
            type: "english",
            title: this.$t("assessments.eng-title") as string,
            description: this.$t("assessments.eng-description") as string
          };
        } else if (assessment.type === "annotation") {
          return {
            ...assessment,
            type: "annotation",
            title: this.$t("assessments.annotation-title") as string,
            description: this.$t("assessments.annotation-description") as string
          };
        } else if (assessment.type === "ai") {
          return {
            ...assessment,
            type: "ai",
            title: this.$t("assessments.ai-title") as string,
            description: this.$t("assessments.ai-description") as string
          };
        }
        return assessment;
      }) as AssessmentCardType[];
      // {
      //   status: AppAssessments.AppAssessmentStatus.IN_PROGRESS,
      //   is_show: true,
      //   type: "annotation",
      //   title: this.$t("assessments.annotation-title") as string,
      //   description: this.$t("assessments.annotation-description") as string,
      //   icon: require("@/assets/icons/inception_annotation_assessment.svg"),
      //   version:
      //     this.get_company_details.s3_path?.assessment_setting
      //       ?.candidates_annotation
      // },
      // {
      //   status: AppAssessments.AppAssessmentStatus.IN_PROGRESS,
      //   is_show: true,
      //   type: "ai",
      //   title: this.$t("assessments.ai-title") as string,
      //   description: this.$t("assessments.ai-description") as string,
      //   icon: require("@/assets/icons/inception_ai_assessments.png"),
      //   version: ""
      // }
    },
    async navigateToIntro() {
      this.dialog.value = false;
      // User Data
      let user_data = {
        ...this.get_user_details.user_data,
        is_intro_showed: true
      };

      // const response =
      await this.update_user_details({ user_data });
      // if (response) {
      //   this.set_root_notification("Data Upload Successfully");
      // }

      this.$router.push({ name: "dashboard-intro" });
    },
    show_intro() {
      if (
        this.get_user_details.user_data &&
        this.get_user_details.user_data.is_intro_showed
      ) {
        this.dialog.value = false;
      } else this.dialog.value = true;
    },
    /**
     * Clear all assessment-related localStorage keys
     * Only called when assessment is cancelled or completed
     */
    clearAssessmentLocalStorage() {
      // Clear legacy localStorage keys
      localStorage.removeItem("stage");
      localStorage.removeItem("partTime");
      localStorage.removeItem("partsTime");
      localStorage.removeItem("questionTime");
      localStorage.removeItem("englishAssessmentTimer");
      localStorage.removeItem("question_id");
      localStorage.removeItem("answers");
      localStorage.removeItem("assessmentTime");
      localStorage.removeItem("assessment_cancelled");
      localStorage.removeItem("eng-terms-accepted");

      // Clear persistence service keys for all assessments
      AssessmentPersistenceService.clearLegacyKeys();
    }
  }
});
